import React from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { PreviewSection } from "../shared";
import { TextInput } from "@/components/ui/TextInput";
import { DateInput } from "@/components/ui/DateInput";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { IncidentType } from "@/types/db/incidents";

// Inline input components
import { IncidentTypeInput } from "@/components/inputs/IncidentTypeInput";
import { UserInput } from "@/components/inputs/UserInput";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { ShiftInput } from "@/components/inputs/ShiftInput";
import { LocationInput } from "@/components/inputs/LocationInput";
import { UsersInput } from "@/components/inputs/UsersInput";
import { ContactsInput } from "@/components/inputs/ContactsInput";
import useAuth from "@/hooks/useAuth";

export interface IncidentData {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  details?: string | null;
  occurred?: string | null;
  meta_data?: {
    incident_type_id?: string;
    submitted_by?: string;
    client_id?: string;
    project_id?: string;
    shift_id?: string;
    occurred_date?: string;
    personnel_involved?: {
      id: string;
      first_name?: string;
      last_name?: string;
    }[];
    contacts_involved?: {
      id: string;
      first_name?: string;
      last_name?: string;
      preferred_name?: string;
      email_address?: string;
      phone_number?: string;
    }[];
    location_id?: string;
  };
}

export interface IncidentOverviewTabProps {
  incident: IncidentData | null;
  loading?: boolean;
}

export function IncidentOverviewTab({ incident, loading = false }: IncidentOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<IncidentType>();
  const { displayNames } = useAuth();

  // Unified data source
  const displayData = editing ? formData : incident;

  // ---------- Handlers ----------

  const handleIncidentTypeChange = (typeId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        incident_type_id: typeId || undefined,
      },
    });
  };

  const handleSubmittedByChange = (userId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        submitted_by: userId || undefined,
      },
    });
  };

  const handleClientChange = (clientId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        client_id: clientId || undefined,
        project_id: undefined, // Clear project when client changes
        shift_id: undefined, // Clear shift when client changes
      },
    });
  };

  const handleProjectChange = (projectId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        project_id: projectId || undefined,
        shift_id: undefined, // Clear shift when project changes
      },
    });
  };

  const handleShiftChange = (shiftId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        shift_id: shiftId || undefined,
      },
    });
  };

  const handleLocationChange = (locationId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        location_id: locationId || undefined,
      },
    });
  };

  const handleDateChange = (date: Date) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        occurred_date: date.toISOString().split("T")[0],
      },
    });
  };

  const handlePersonnelChange = (users: { id: string; first_name?: string; last_name?: string }[]) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        personnel_involved: users.length > 0 ? users : undefined,
      },
    });
  };

  const handleContactsChange = (
    contacts: {
      id: string;
      first_name?: string;
      last_name?: string;
      preferred_name?: string;
      email_address?: string;
      phone_number?: string;
    }[],
  ) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        contacts_involved: contacts.length > 0 ? contacts : undefined,
      },
    });
  };

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 16 }}>
        {/* Incident Type */}
        <IncidentTypeInput
          value={(displayData?.meta_data as any)?.incident_type_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Incident Type"
          onChange={handleIncidentTypeChange}
        />

        {/* Reported By */}
        <UserInput
          value={(displayData?.meta_data as any)?.submitted_by || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Reported By"
          onChange={handleSubmittedByChange}
        />

        {/* Client */}
        <ClientInput
          value={(displayData?.meta_data as any)?.client_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.clients?.name || "Client"}
          placeholder={`Select ${displayNames?.clients?.name?.toLowerCase() || "client"} (optional)`}
          onChange={handleClientChange}
        />

        {/* Project */}
        <ProjectInput
          value={(displayData?.meta_data as any)?.project_id || null}
          clientId={(displayData?.meta_data as any)?.client_id}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.projects?.name || "Project"}
          placeholder={`Select ${displayNames?.projects?.name?.toLowerCase() || "project"} (optional)`}
          onChange={handleProjectChange}
        />

        {/* Shift */}
        <ShiftInput
          value={(displayData?.meta_data as any)?.shift_id || null}
          projectId={(displayData?.meta_data as any)?.project_id}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.shifts?.name || "Shift"}
          placeholder={`Select ${displayNames?.shifts?.name?.toLowerCase() || "shift"} (optional)`}
          onChange={handleShiftChange}
        />

        {/* Location */}
        <LocationInput
          value={(displayData?.meta_data as any)?.location_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Location"
          placeholder="Select location (optional)"
          onChange={handleLocationChange}
        />

        {/* Date of Occurrence */}
        <DateInput
          label="Date of Occurrence"
          mode="date"
          value={(displayData?.meta_data as any)?.occurred_date}
          onChange={handleDateChange}
          editing={editing}
          loading={loading}
          placeholder="Select date"
          labelStyle={styles.label}
        />

        {/* Personnel Involved */}
        <UsersInput
          value={(displayData?.meta_data as any)?.personnel_involved || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={`${displayNames?.users?.plural || "Users"} Involved`}
          onChange={handlePersonnelChange}
        />

        {/* Contacts Involved */}
        <ContactsInput
          value={(displayData?.meta_data as any)?.contacts_involved || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={`${displayNames?.contacts?.plural || "Contacts"} Involved`}
          onChange={handleContactsChange}
        />

        {/* Description */}
        <PreviewSection title="Description" titleStyle={styles.label}>
          <TextInput
            value={displayData?.description || ""}
            onChangeText={(text) => updateFormData({ ...formData, description: text })}
            multiline
            numberOfLines={3}
            editable={editing}
            loading={loading}
            placeholder="Describe the incident..."
          />
        </PreviewSection>

        {/* Additional Details */}
        <PreviewSection title="Additional Details" titleStyle={styles.label}>
          <TextInput
            value={displayData?.details || ""}
            onChangeText={(text) => updateFormData({ ...formData, details: text })}
            multiline
            numberOfLines={5}
            editable={editing}
            loading={loading}
            placeholder="Add any additional details..."
          />
        </PreviewSection>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});
